import { StoreModel } from '.'
import { CookieName } from '@mortgage-pos/types'
import { environment } from '@mortgage-pos/ui/env'
import rollbar from '@mortgage-pos/ui/services/rollbar'
import newRelic from '@mortgage-pos/ui/services/newRelic'
import incense from '@mortgage-pos/ui/services/error-handling/incense'
import { borrowerPortalRequest } from '@mortgage-pos/ui/services/http'
import { action, Action, thunk, Thunk } from 'easy-peasy'
import { CustomError } from '@mortgage-pos/types'
import { borrowerPortalLoginUrl } from '@mortgage-pos/data'
import { deleteCookie, redirectTo, setCSRFToken } from '@mortgage-pos/utils'

export interface ApplicationInfo {
  applicationId: number
  applicationGuid: string
  bankrateLeadId?: string | null
  createdAt: Date
  completed: boolean
}
interface CheckAuthenticationOptions {
  delayResponse?: boolean
  // Questionnaire renders a full-page loader while isCheckingAuthentication is
  // true, unmounting the active page. Background re-checks must not do that.
  silent?: boolean
}

export interface AuthenticationModel {
  isCheckingAuthentication: boolean
  isAuthenticated: boolean
  applicationInfo: ApplicationInfo
  setIsCheckingAuthentication: Action<AuthenticationModel, boolean>
  setIsAuthenticated: Action<AuthenticationModel, boolean>
  setApplicationInfo: Action<AuthenticationModel, ApplicationInfo>
  checkAuthentication: Thunk<
    AuthenticationModel,
    CheckAuthenticationOptions | void,
    null,
    StoreModel
  >
  borrowerPortalLogin: Thunk<AuthenticationModel, string, null, StoreModel>
  checkApplicationStatus: Thunk<AuthenticationModel, null, null, StoreModel>
  checkAccountByGuid: Thunk<
    AuthenticationModel,
    string,
    null,
    StoreModel,
    Promise<boolean>
  >
  logout: Thunk<AuthenticationModel, null, null, StoreModel>
}

const authentication = (): AuthenticationModel => ({
  isCheckingAuthentication: false,

  isAuthenticated: false,

  applicationInfo: null,

  setIsAuthenticated: action((state, payload) => {
    state.isAuthenticated = payload
  }),

  setIsCheckingAuthentication: action((state, payload) => {
    state.isCheckingAuthentication = payload
  }),

  setApplicationInfo: action((state, payload) => {
    state.applicationInfo = payload
  }),

  checkAuthentication: thunk(
    async (
      { setIsAuthenticated, setIsCheckingAuthentication },
      payload,
      { getStoreState }
    ) => {
      const { delayResponse, silent } = payload ?? {}

      // A silent check must leave isCheckingAuthentication alone entirely, or it
      // would clobber the flag for a concurrent visible check.
      const setChecking = (value: boolean) => {
        if (!silent) setIsCheckingAuthentication(value)
      }

      setChecking(true)

      let response
      try {
        response = await borrowerPortalRequest('/check-authentication')
      } catch (e) {
        // Only set the isAuthenticated to false if the response is a 401
        if (e.response?.status === 401) {
          // Count csrf-branch 401s (client/session token divergence) to track
          // the fix in prod; behavior unchanged, still treated as signed-out.
          if (e.response?.data?.message === 'CSRF') {
            newRelic.increment('check_authentication.csrf_mismatch')
          }
          setIsAuthenticated(false)
          setChecking(false)
          return
        }

        const { applicationId, applicationGuid } = getStoreState().application

        // A non-401 leaves isAuthenticated untouched, so a single transient
        // failure here strands the borrower until something re-checks.
        incense(e)
          .details({
            name: 'CheckAuthenticationError',
            message: 'Check authentication error',
          })
          .safe({ status: e.response?.status, applicationGuid })
          .sensitive({ applicationId })
          .error()

        newRelic.increment('check_authentication.error', [
          { key: 'status', value: String(e.response?.status ?? 'none') },
        ])

        setChecking(false)
        return
      }

      if (!delayResponse) {
        return _complete()
      }

      //delay to prevent flashing behavior when the loading gif is
      //quickly shown, due to fast connections
      setTimeout(_complete, 2000)

      function _complete() {
        if (response.status === 201) {
          setIsAuthenticated(true)
        }
        setChecking(false)
      }
    }
  ),

  borrowerPortalLogin: thunk(
    async (
      { checkAuthentication },
      accessToken,
      { getState, getStoreState }
    ) => {
      const { isAuthenticated } = getState()
      const { applicationId } = getStoreState().application

      if (accessToken && !isAuthenticated) {
        try {
          const response = await borrowerPortalRequest(
            '/login',
            {},
            { Authorization: `Bearer ${accessToken}` }
          )

          // Adopt the csrf /login returns so gated calls match; otherwise the
          // client keeps a stale token and every request 401s.
          if (response?.data?.csrf) {
            setCSRFToken(response.data.csrf)
          }
        } catch (error) {
          const response = error.response ? error.response.body : error

          const errorObject = new CustomError(
            'BorrowerPortalLoginError',
            'Failed to set session cookie'
          )

          const errorMetadata = {
            applicationId,
            error,
            sensitive: { response },
          }

          rollbar.error(errorObject, errorMetadata)
        }

        await checkAuthentication()
      }
    }
  ),

  checkApplicationStatus: thunk(async ({ setApplicationInfo }) => {
    let appStatusResponse
    try {
      appStatusResponse = await borrowerPortalRequest('/application-status')
    } catch (e) {
      rollbar.error('Failed to get application info by userId', e)
    }

    setApplicationInfo(appStatusResponse.data)
  }),

  checkAccountByGuid: thunk(async (_, applicationGuid): Promise<boolean> => {
    try {
      const response = await borrowerPortalRequest('/check-account', {
        applicationGuid,
      })

      return response?.data?.hasAccount
    } catch (e) {
      incense(e)
        .details({
          name: 'AuthenticationStoreError',
          message: 'error during checkAccountById',
        })
        .safe({
          applicationGuid,
        })
        .error()

      return false
    }
  }),

  logout: thunk(async ({ setIsAuthenticated }) => {
    await borrowerPortalRequest(`/logout`, {}, {}, 'post')
    deleteCookie(CookieName.AuthToken)
    setIsAuthenticated(false)
    if (
      environment.productName === 'sage' ||
      environment.productName === 'sage-home-loans'
    ) {
      redirectTo(borrowerPortalLoginUrl)
    }
  }),
})

export default authentication
